import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Search, Library as LibIcon, ChevronRight, ChevronDown,
  ArrowUp, ArrowDown, Upload, FileDown, ArrowUpFromLine, ArrowDownToLine,
  Tags, Trash2, AlertTriangle, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import Flag from "lucide-react/dist/esm/icons/flag";
import Bookmark from "lucide-react/dist/esm/icons/bookmark";
import { toast } from "sonner";

import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import { PageHeader, Card, PrimaryButton, QuietButton, EmptyState, TagChip, Modal, Input, Select } from "../components/UI";
import { LibraryImportModal } from "../components/LibraryImportModal";
import { MasterLibraryStaleBanner } from "../components/MasterLibraryStaleBanner";
import { StaleReviewModal } from "../components/StaleReviewModal";

const cls = (...a) => a.filter(Boolean).join(" ");

// LT-32 Stage 2 Group 2 — rate-confidence tier presentation. The tier value is
// computed upstream by the extraction tool and stored verbatim; we only render
// it. Colours: HIGH green, SINGLE_SOURCE blue, INDICATIVE amber, DESC grey.
const CONFIDENCE_META = {
  HIGH:             { label: "High",          bg: "#E8EFE9", fg: "#23402F", bd: "#C6D8CB" },
  SINGLE_SOURCE:    { label: "Single source", bg: "#E4EDF6", fg: "#1E3A5F", bd: "#C3D6EA" },
  INDICATIVE:       { label: "Indicative",    bg: "#FBF0DD", fg: "#7A3E06", bd: "#F0D9A8" },
  DESCRIPTION_ONLY: { label: "Desc. only",    bg: "#EEEDE8", fg: "#6B6B6B", bd: "#DCDAD2" },
};
const CONFIDENCE_FILTER_OPTIONS = [
  { value: "ALL", label: "All confidence" },
  { value: "HIGH", label: "High" },
  { value: "SINGLE_SOURCE", label: "Single source" },
  { value: "INDICATIVE", label: "Indicative" },
  { value: "DESCRIPTION_ONLY", label: "Description only" },
];

function ConfidencePill({ value }) {
  const meta = CONFIDENCE_META[value];
  if (!meta) return <span className="text-ink-4 font-mono" style={{ fontSize: 11 }}>—</span>;
  return (
    <span
      className="inline-flex items-center rounded font-mono uppercase"
      data-testid={`library-confidence-${value}`}
      title={`Rate confidence: ${meta.label} (from import)`}
      style={{
        padding: "1px 6px", fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
        background: meta.bg, color: meta.fg, border: `1px solid ${meta.bd}`,
      }}
    >
      {meta.label}
    </span>
  );
}

// LT-27 Library page.
// • Filter toggle: All | Draft | Live. Default = Live.
// • Draft view defaults to frequency desc (highest-value review queue).
// • Draft rows: muted style + DRAFT chip. NOT BUILT UP indicator on any
//   is_built_up=false item (Draft or Live) next to the RECIPE pill slot.
// • Bulk actions (Draft view only): multi-select checkboxes, bulk phase tag,
//   bulk delete (hard delete). No bulk promote — promotion is per-item.
// • Row actions on drafts: Promote as flat rate · Demote (on live items) via
//   confirm dialogs. "Promote with recipe" is only available on LibraryEdit.
// • Import CSV + Download template buttons in the page header.
export default function Library() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Honour ?status=draft|live|stale|all (e.g. the draft-review save redirect
  // lands here on the Draft view). Falls back to "live".
  const initialStatus = (() => {
    const s = searchParams.get("status");
    return ["live", "draft", "stale", "all", "review", "flat"].includes(s) ? s : "live";
  })();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(initialStatus);             // "all" | "draft" | "live" | "stale" | "review" | "flat"
  const [counts, setCounts] = useState({ live: 0, draft: 0, stale: 0, review: 0, flat: 0 });
  // LT-32 G2 — draft review confidence filter (defaults to HIGH per spec).
  const [confidenceFilter, setConfidenceFilter] = useState("HIGH");

  // LT-28 — stale-pricing cascade UI state.
  const [staleBannerDismissed, setStaleBannerDismissed] = useState(false); // session-only
  const [staleReviewOpen, setStaleReviewOpen] = useState(false);
  // Ledger + trend cache per library_item id: {latest, count, direction, deltaPct}
  const [ledgerCache, setLedgerCache] = useState({});

  // Sort: user-toggleable columns
  const [sortKey, setSortKey] = useState("description");
  const [sortDir, setSortDir] = useState("asc");

  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Multi-select (Draft view only)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dialogs
  const [importOpen, setImportOpen] = useState(false);
  const [flatRateDialog, setFlatRateDialog] = useState(null);  // item | null
  const [demoteDialog, setDemoteDialog] = useState(null);      // item | null
  const [draftDeleteDialog, setDraftDeleteDialog] = useState(null); // item | null
  const [bulkTagDialog, setBulkTagDialog] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);

  // Master phase list (for bulk-tag + LibraryEdit — cached here for simplicity)
  const [masterPhases, setMasterPhases] = useState([]);

  const load = async (targetStatus = status) => {
    setLoading(true);
    try {
      // The `stale` view is really the `live` set filtered client-side; the
      // backend has no `stale` value for the status filter. LT-36 `flat` =
      // every non-archived item, filtered client-side on flat_material_enabled.
      const backendStatus = targetStatus === "stale" ? "live" : targetStatus === "flat" ? "all" : targetStatus;
      const r = await api.post("/library/backfill", null, { params: { status: backendStatus } });
      setItems(r.data?.items || []);
    } catch (e) {
      // Fallback path (matches prior behaviour).
      try {
        const backendStatus = targetStatus === "stale" ? "live" : targetStatus === "flat" ? "all" : targetStatus;
        const r = await api.get("/library", { params: { status: backendStatus } });
        setItems(r.data || []);
      } catch (e2) {
        toast.error("Failed to load library");
      }
    } finally {
      setLoading(false);
    }
  };

  // LT-34 — one-click clear of a review flag from the Review tab / row.
  const clearReview = async (itemId) => {
    try {
      await api.post(`/library/${itemId}/review-clear`);
      toast.success("Review flag cleared");
      await Promise.all([load(status), loadCounts()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear flag");
    }
  };

  // Load counts (live + draft + stale) independently so the toggle badge is
  // accurate even when the visible view is filtered.
  const loadCounts = async () => {
    try {
      const [live, draft, staleCount, review, flat] = await Promise.all([
        api.get("/library", { params: { status: "live" } }),
        api.get("/library", { params: { status: "draft" } }),
        api.get("/library/stale/count"),
        api.get("/library", { params: { status: "review" } }),
        api.get("/library", { params: { status: "all", material: "flat" } }),   // LT-36
      ]);
      setCounts({
        live: (live.data || []).length,
        draft: (draft.data || []).length,
        stale: staleCount.data?.count || 0,
        review: (review.data || []).length,
        flat: (flat.data || []).length,
      });
    } catch (e) {
      // non-fatal
    }
  };

  // LT-28 — fetch a page of library-item ledger entries in one shot so the
  // trend column can render without N+1 requests. We take up to 1000 rows,
  // which comfortably covers a live library of 500 items with 2-3 entries
  // each. The endpoint sorts newest-first.
  const loadLedgerCache = async () => {
    try {
      const r = await api.get("/price-history", {
        params: { entity_type: "library_item", limit: 1000 },
      });
      const byId = {};
      for (const entry of r.data || []) {
        const arr = (byId[entry.entity_id] ||= []);
        arr.push(entry);
      }
      // Compute {count, latest, earliest, direction, deltaPct} per item.
      const out = {};
      for (const [id, entries] of Object.entries(byId)) {
        // entries are newest-first per the endpoint contract; earliest is last.
        const latest = entries[0];
        const earliest = entries[entries.length - 1];
        const count = entries.length;
        let direction = "flat";
        let deltaPct = null;
        if (count >= 2 && earliest.new_value != null && latest.new_value != null) {
          const from = earliest.new_value;
          const to = latest.new_value;
          if (from !== 0) {
            deltaPct = ((to - from) / from) * 100;
            direction = to > from ? "up" : to < from ? "down" : "flat";
          }
        }
        out[id] = {
          count, latest: latest.new_value, earliest: earliest.new_value,
          direction, deltaPct,
        };
      }
      setLedgerCache(out);
    } catch (e) {
      // Non-fatal — trend column just stays "No history yet".
      setLedgerCache({});
    }
  };

  useEffect(() => { load(status); }, [status]); // eslint-disable-line
  useEffect(() => { loadCounts(); loadLedgerCache(); }, []); // eslint-disable-line
  useEffect(() => {
    api.get("/settings/master-phases").then((r) => setMasterPhases(r.data || [])).catch(() => {});
  }, []);

  // When switching to Draft view for the first time, default sort to frequency desc.
  useEffect(() => {
    if (status === "draft") {
      setSortKey("frequency");
      setSortDir("desc");
      setConfidenceFilter("HIGH");   // LT-32 G2 — default the review queue to HIGH
    } else if (status === "live") {
      setSortKey("description");
      setSortDir("asc");
    }
    // Clear multi-select when leaving Draft view.
    if (status !== "draft") setSelectedIds(new Set());
  }, [status]);

  // Filter + group + sort.
  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = (it) => !needle
      || (it.description || "").toLowerCase().includes(needle)
      || (it.sku || "").toLowerCase().includes(needle);

    // LT-28 — Stale filter is applied client-side against the live set.
    // LT-36 — Flat material filter likewise, against the full set.
    const staleGate = (it) =>
      (status !== "stale" || it.pricing_stale === true)
      && (status !== "flat" || it.flat_material_enabled === true);
    // LT-32 G2 — confidence filter applies only in Draft view.
    const confGate = (it) =>
      status !== "draft"
      || confidenceFilter === "ALL"
      || (it.rate_confidence || "") === confidenceFilter;

    const map = {};
    for (const it of items) {
      if (!matches(it)) continue;
      if (!staleGate(it)) continue;
      if (!confGate(it)) continue;
      const tag = (it.phase_tags && it.phase_tags.length) ? it.phase_tags[0] : "__uncat__";
      (map[tag] ||= []).push(it);
    }

    const cmp = (a, b) => {
      let av, bv;
      if (sortKey === "description") {
        av = (a.description || "").toLowerCase();
        bv = (b.description || "").toLowerCase();
      } else if (sortKey === "sku") {
        av = (a.sku || "").toLowerCase();
        bv = (b.sku || "").toLowerCase();
      } else if (sortKey === "frequency") {
        av = a.source_frequency || 0;
        bv = b.source_frequency || 0;
      } else {
        av = a.updated_at || "";
        bv = b.updated_at || "";
      }
      const r = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? r : -r;
    };
    for (const k in map) map[k].sort(cmp);

    const keys = Object.keys(map).filter((k) => k !== "__uncat__")
      .sort((a, b) => a.localeCompare(b));
    if (map["__uncat__"]) keys.push("__uncat__");
    return keys.map((k) => ({
      tag: k === "__uncat__" ? "Unassigned" : k,
      key: k,
      items: map[k],
      isUncat: k === "__uncat__",
    }));
  }, [items, q, sortKey, sortDir, status, confidenceFilter]);

  const totalFiltered = grouped.reduce((s, g) => s + g.items.length, 0);

  const toggleGroup = (key) => setCollapsedGroups((p) => ({ ...p, [key]: !p[key] }));
  const expandAll = () => setCollapsedGroups({});
  const collapseAll = () => {
    const all = {};
    for (const g of grouped) all[g.key] = true;
    setCollapsedGroups(all);
  };
  const isExpanded = (key) => !!q.trim() || !collapsedGroups[key];

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "updated_at" || key === "frequency" ? "desc" : "asc");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const nxt = new Set(prev);
      if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
      return nxt;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => {
    const nxt = new Set();
    for (const g of grouped) for (const it of g.items) nxt.add(it.id);
    setSelectedIds(nxt);
  };

  const showDraftFeatures = status === "draft";
  const totalCols = showDraftFeatures ? 10 : 8;

  const onImportCommitted = async () => {
    // Best-effort refresh (switch to Draft so the user immediately sees the
    // review queue).
    setStatus("draft");
    await loadCounts();
  };

  const downloadTemplateShortcut = async () => {
    try {
      const r = await api.get("/library/import/template", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "luxtender_master_library_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const performPromoteFlat = async (item, rate) => {
    try {
      await api.post(`/library/${item.id}/promote`, { mode: "flat", rate: Number(rate) });
      toast.success(`Promoted '${item.description.slice(0, 40)}' to Live`);
      setFlatRateDialog(null);
      await Promise.all([load(status), loadCounts()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    }
  };
  const performDemote = async (item) => {
    try {
      await api.post(`/library/${item.id}/demote`);
      toast.success(`Demoted '${item.description.slice(0, 40)}' to Draft`);
      setDemoteDialog(null);
      await Promise.all([load(status), loadCounts()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demote failed");
    }
  };
  const performDraftDelete = async (item) => {
    try {
      await api.delete(`/library/${item.id}/draft`);
      toast.success(`Deleted draft '${item.description.slice(0, 40)}'`);
      setDraftDeleteDialog(null);
      await Promise.all([load(status), loadCounts()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const performBulkTag = async (phaseTag) => {
    try {
      const r = await api.patch("/library/bulk/phase-tag", {
        ids: Array.from(selectedIds),
        phase_tag: phaseTag,
      });
      toast.success(`Tagged ${r.data.modified} item${r.data.modified === 1 ? "" : "s"}`);
      setBulkTagDialog(false);
      clearSelection();
      await load(status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk tag failed");
    }
  };
  const performBulkDelete = async () => {
    try {
      const r = await api.delete("/library/bulk", {
        data: { ids: Array.from(selectedIds) },
      });
      toast.success(`Deleted ${r.data.deleted} draft item${r.data.deleted === 1 ? "" : "s"}`);
      setBulkDeleteDialog(false);
      clearSelection();
      await Promise.all([load(status), loadCounts()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const SortHeader = ({ keyName, label, ...rest }) => (
    <th className="col-head px-4 py-2.5" {...rest}>
      <button
        onClick={() => handleSort(keyName)}
        data-testid={`library-sort-${keyName}`}
        className={cls(
          "inline-flex items-center gap-1 transition-colors uppercase",
          sortKey === keyName ? "text-ink" : "text-ink-3 hover:text-ink-2"
        )}
        style={{ letterSpacing: "0.06em", fontWeight: 700, fontSize: 10.5 }}
      >
        {label}
        {sortKey === keyName && (
          sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />
        )}
      </button>
    </th>
  );

  const StatusPill = ({ value }) => {
    const isDraft = value === "draft";
    return (
      <span
        className="inline-flex items-center rounded font-mono uppercase"
        style={{
          padding: "1px 6px",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.05em",
          background: isDraft ? "#FEF3E2" : "#E8EFE9",
          color: isDraft ? "#7A3E06" : "#23402F",
          border: `1px solid ${isDraft ? "#F0D9A8" : "#D6E2D8"}`,
        }}
      >
        {value || "live"}
      </span>
    );
  };

  return (
    <div className="app-container py-8">
      <PageHeader
        title="Master Library"
        subtitle="Reusable priced items, provisional sums and prelims used across tenders."
        actions={
          <>
            <QuietButton icon={FileDown} onClick={downloadTemplateShortcut} data-testid="library-template-btn">
              Template
            </QuietButton>
            <QuietButton icon={Upload} onClick={() => setImportOpen(true)} data-testid="library-import-btn">
              Import CSV
            </QuietButton>
            <PrimaryButton icon={Plus} onClick={() => navigate("/library/new")} data-testid="library-new-btn">
              Add library item
            </PrimaryButton>
          </>
        }
      />

      {/* LT-28 — Stale-pricing banner (shown when any live item is flagged
          AND user hasn't dismissed the banner this session). Dismissal is
          session-only; STALE chips and Stale filter remain regardless. */}
      <MasterLibraryStaleBanner
        count={staleBannerDismissed ? 0 : counts.stale}
        onReview={() => setStaleReviewOpen(true)}
        onDismiss={() => setStaleBannerDismissed(true)}
      />

      {/* Toggle strip + search */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div
          className="inline-flex items-center rounded-md border border-line bg-surface p-0.5"
          data-testid="library-status-toggle"
        >
          {[
            { key: "live",  label: "Live",  count: counts.live },
            { key: "draft", label: "Draft", count: counts.draft },
            { key: "stale", label: "Stale", count: counts.stale, tone: counts.stale > 0 ? "amber" : "default" },
            { key: "review", label: "Review", count: counts.review, tone: counts.review > 0 ? "violet" : "default" },
            { key: "flat",  label: "Flat material", count: counts.flat },
            { key: "all",   label: "All",   count: counts.live + counts.draft },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setStatus(opt.key)}
              data-testid={`library-status-${opt.key}`}
              className={cls(
                "h-8 px-3 rounded transition-colors inline-flex items-center gap-2",
                status === opt.key ? "bg-accent text-white" : "text-ink-3 hover:text-ink hover:bg-surface-2/60"
              )}
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              {opt.label}
              <span
                className={cls(
                  "font-mono tnum rounded px-1.5",
                  status === opt.key
                    ? "bg-white/20"
                    : opt.tone === "amber"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-surface-2 text-ink-3"
                )}
                style={{ fontSize: 10.5 }}
                data-testid={`library-status-count-${opt.key}`}
              >
                {opt.count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-1 max-w-md">
          {showDraftFeatures && (
            <div style={{ width: 168 }} data-testid="library-confidence-filter">
              <Select
                value={confidenceFilter}
                onChange={setConfidenceFilter}
                options={CONFIDENCE_FILTER_OPTIONS}
              />
            </div>
          )}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by description or SKU…" spellCheck="true"
              data-testid="library-search-input"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-ink-3" style={{ fontSize: 11.5 }} data-testid="library-counts">
            {totalFiltered} item{totalFiltered === 1 ? "" : "s"} · {grouped.length} group{grouped.length === 1 ? "" : "s"}
          </span>
          <button
            onClick={expandAll} data-testid="library-expand-all"
            className="h-7 px-2.5 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2 transition-colors"
            style={{ fontSize: 11.5 }}
          >
            Expand all
          </button>
          <button
            onClick={collapseAll} data-testid="library-collapse-all"
            className="h-7 px-2.5 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2 transition-colors"
            style={{ fontSize: 11.5 }}
          >
            Collapse all
          </button>
        </div>
      </div>

      {/* Bulk actions bar — Draft view only, appears when any row selected */}
      {showDraftFeatures && selectedIds.size > 0 && (
        <div
          className="mb-3 flex items-center justify-between px-4 py-2.5 rounded-md border border-accent bg-accent-soft"
          data-testid="library-bulk-bar"
        >
          <div className="flex items-center gap-3">
            <span className="text-accent-deep" style={{ fontSize: 12.5, fontWeight: 500 }}>
              {selectedIds.size} draft item{selectedIds.size === 1 ? "" : "s"} selected
            </span>
            <button
              onClick={clearSelection}
              data-testid="library-bulk-clear"
              className="text-ink-3 hover:text-ink underline"
              style={{ fontSize: 11.5 }}
            >
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2">
            <QuietButton icon={Tags} onClick={() => setBulkTagDialog(true)} data-testid="library-bulk-tag">
              Assign phase tag
            </QuietButton>
            <QuietButton
              icon={Trash2}
              onClick={() => setBulkDeleteDialog(true)}
              data-testid="library-bulk-delete"
              className="hover:!bg-red-50 hover:!text-red-700"
            >
              Delete
            </QuietButton>
          </div>
        </div>
      )}

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }} data-testid="library-loading">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={LibIcon}
            title={
              status === "draft"
                ? "No draft items"
                : status === "live"
                ? "No live library items yet"
                : status === "flat"
                ? "No items on a Flat Material Rate"
                : "No library items yet"
            }
            hint={
              status === "draft"
                ? "Import a CSV to seed draft candidates for review."
                : "Add your first library item to start building reusable scope."
            }
            action={
              status === "draft" ? (
                <PrimaryButton icon={Upload} onClick={() => setImportOpen(true)}>Import CSV</PrimaryButton>
              ) : (
                <PrimaryButton icon={Plus} onClick={() => navigate("/library/new")}>Add library item</PrimaryButton>
              )
            }
          />
        ) : grouped.length === 0 ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12.5 }} data-testid="library-no-matches">
            No matching items.
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-2/50">
                {showDraftFeatures && (
                  <th className="col-head px-3 py-2.5 text-center" style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedIds.size === items.length}
                      onChange={(e) => (e.target.checked ? selectAllVisible() : clearSelection())}
                      data-testid="library-select-all"
                      className="accent-accent cursor-pointer"
                    />
                  </th>
                )}
                <SortHeader keyName="sku" label="SKU" style={{ width: 110 }} />
                <SortHeader keyName="description" label="Description" />
                <th className="col-head px-4 py-2.5">Phase tags</th>
                <th className="col-head px-4 py-2.5" style={{ width: 74 }}>Status</th>
                {showDraftFeatures && (
                  <th className="col-head px-4 py-2.5" style={{ width: 108 }}>Confidence</th>
                )}
                <th className="col-head px-4 py-2.5" style={{ width: 60 }}>Unit</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 130 }}>
                  {status === "draft" ? "Ref rate" : "Rate"}
                </th>
                {status === "draft" ? (
                  <SortHeader keyName="frequency" label="Freq" style={{ width: 68 }} />
                ) : (
                  <SortHeader keyName="updated_at" label="Updated" style={{ width: 100 }} />
                )}
                <th style={{ width: 34 }} />
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <React.Fragment key={g.key}>
                  <tr
                    onClick={() => toggleGroup(g.key)}
                    data-testid={`library-group-${g.key}`}
                    className="cursor-pointer hover:bg-surface-2/40 transition-colors border-b border-line-soft"
                    style={{ background: "#F5F3EE" }}
                  >
                    <td colSpan={totalCols} className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {isExpanded(g.key)
                          ? <ChevronDown size={12} className="text-ink-3" />
                          : <ChevronRight size={12} className="text-ink-3" />}
                        <span
                          className={cls(
                            "font-mono uppercase",
                            g.isUncat ? "text-ink-3" : "text-ink-2"
                          )}
                          style={{ fontSize: 10.5, letterSpacing: "0.06em", fontWeight: 700 }}
                        >
                          {g.tag}
                        </span>
                        <span className="text-ink-3" style={{ fontSize: 11 }}>
                          ({g.items.length})
                        </span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded(g.key) && g.items.map((it) => {
                    const isDraft = it.status === "draft";
                    const notBuiltUp = it.is_built_up === false;
                    const isStale = it.pricing_stale === true;
                    const rowMuted = isDraft;
                    // LT-Estimate-UX-01 Section 4 — "estimated_each_use"
                    // items are unmeasured / judgement-call; the £ rate is
                    // not a real rate. Surface as "est." in the price
                    // column so the estimator can tell mode-2 items apart
                    // at a glance in the picker.
                    const isEstMode = it.labour_mode === "estimated_each_use";
                    const displayRate = isDraft
                      ? (it.ref_rate_total != null ? gbp(it.ref_rate_total) : "—")
                      : (isEstMode ? "est." : gbp(it.current_rate));
                    const isSelected = selectedIds.has(it.id);
                    const trend = ledgerCache[it.id];
                    return (
                      <tr
                        key={it.id}
                        onClick={(e) => {
                          // Don't navigate if a click landed on the checkbox or an action button.
                          if (e.target.closest("[data-row-action]") || e.target.closest("input[type=checkbox]")) return;
                          navigate(`/library/${it.id}`);
                        }}
                        data-testid={`library-row-${it.id}`}
                        className={cls(
                          "group cursor-pointer border-b border-line-soft",
                          rowMuted ? "bg-surface-2/30 hover:bg-surface-2/60" : "hover:bg-surface-2/60"
                        )}
                        style={rowMuted ? { color: "#6B6B6B" } : undefined}
                      >
                        {showDraftFeatures && (
                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(it.id)}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`library-select-${it.id}`}
                              className="accent-accent cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <span
                            className="font-mono tnum text-ink-3"
                            style={{ fontSize: 11.5 }}
                            title={!it.sku && (it.cluster_ref || it.import_ref) ? "Draft reference (no SKU until promoted to Live)" : undefined}
                          >
                            {/* LT-35 — SKU column display priority:
                                sku → cluster_ref → import_ref → dash. */}
                            {it.sku || it.cluster_ref || it.import_ref || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {it.has_recipe && (
                              <span
                                className="inline-flex items-center justify-center rounded font-mono flex-shrink-0"
                                data-testid={`library-row-recipe-pill-${it.id}`}
                                style={{
                                  background: "#E8EFE9", color: "#2F4F3E",
                                  border: "1px solid #D6E2D8",
                                  padding: "1px 6px", fontSize: 9, fontWeight: 600,
                                  letterSpacing: "0.05em",
                                }}
                              >
                                RECIPE
                              </span>
                            )}
                            {it.flat_material_enabled && (
                              <span
                                className="inline-flex items-center justify-center rounded font-mono flex-shrink-0"
                                data-testid={`library-row-flat-pill-${it.id}`}
                                title={`Materials priced as one flat figure${it.flat_material_rate != null ? ` (${gbp(it.flat_material_rate)}/${it.unit || "unit"})` : ""} — nothing itemised yet. Open the item to build the material recipe.`}
                                style={{
                                  background: "#F8F1DF", color: "#7A5A06",
                                  border: "1px solid #EAD9A6",
                                  padding: "1px 6px", fontSize: 9, fontWeight: 600,
                                  letterSpacing: "0.05em",
                                }}
                              >
                                FLAT MATERIAL
                              </span>
                            )}
                            {notBuiltUp && (
                              <span
                                className="inline-flex items-center gap-1 rounded font-mono flex-shrink-0"
                                data-testid={`library-not-built-up-${it.id}`}
                                title="No recipe or manually-confirmed flat rate yet"
                                style={{
                                  background: "#FEF3E2", color: "#7A3E06",
                                  border: "1px solid #F0D9A8",
                                  padding: "1px 6px", fontSize: 9, fontWeight: 600,
                                  letterSpacing: "0.05em",
                                }}
                              >
                                <AlertTriangle size={9} /> NOT BUILT UP
                              </span>
                            )}
                            {isStale && (
                              <span
                                className="inline-flex items-center gap-1 rounded font-mono flex-shrink-0"
                                data-testid={`library-stale-chip-${it.id}`}
                                title="Underlying material or labour costs have changed since this rate was calculated. Review and accept to update."
                                style={{
                                  background: "#FEF3E2", color: "#7A3E06",
                                  border: "1px solid #F0D9A8",
                                  padding: "1px 6px", fontSize: 9, fontWeight: 600,
                                  letterSpacing: "0.05em",
                                }}
                              >
                                <AlertTriangle size={9} /> STALE
                              </span>
                            )}
                            {it.needs_review && (
                              <span className="inline-flex items-center gap-0.5 flex-shrink-0" data-testid={`library-review-${it.id}`}>
                                <span
                                  className="inline-flex items-center gap-1 rounded text-violet-700 bg-violet-50 border border-violet-200"
                                  style={{ padding: "1px 5px", fontSize: 9.5, fontWeight: 600 }}
                                  title={it.review_reason ? `Flagged for review: ${it.review_reason}` : "Flagged for review"}
                                >
                                  <Bookmark size={10} /> REVIEW
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); clearReview(it.id); }}
                                  data-testid={`library-review-clear-${it.id}`}
                                  className="text-ink-4 hover:text-violet-700"
                                  style={{ fontSize: 10 }}
                                  title="Clear review flag"
                                >
                                  clear
                                </button>
                              </span>
                            )}
                            {it.flags && (
                              <span
                                className="inline-flex items-center flex-shrink-0 text-amber-600"
                                data-testid={`library-flags-${it.id}`}
                                title={`Flags: ${String(it.flags).split(";").map((s) => s.trim()).filter(Boolean).join(", ")}`}
                                aria-label="Import flags"
                              >
                                <Flag size={11} />
                              </span>
                            )}
                            <span className={rowMuted ? "text-ink-2" : "text-ink"} style={{ fontSize: 13 }}>
                              {it.description}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(it.phase_tags || []).slice(0, 3).map((t) => (
                              <TagChip key={t} label={t} tone="accent" />
                            ))}
                            {(it.phase_tags || []).length > 3 && (
                              <span className="text-ink-3" style={{ fontSize: 11 }}>
                                +{it.phase_tags.length - 3}
                              </span>
                            )}
                            {(!it.phase_tags || it.phase_tags.length === 0) && (
                              <span className="text-ink-4 italic" style={{ fontSize: 11.5 }}>Unassigned</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill value={it.status} />
                        </td>
                        {showDraftFeatures && (
                          <td className="px-4 py-3">
                            <ConfidencePill value={it.rate_confidence} />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <span className="text-ink-3 font-mono" style={{ fontSize: 11.5 }}>{it.unit}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className={cls("font-mono tnum", rowMuted ? "text-ink-2" : "text-ink")} style={{ fontSize: 12.5 }}>
                            {displayRate}
                          </div>
                          {/* LT-28 — Trend indicator. Drafts never have a
                              ledger; show a muted "No history yet" for
                              live items with <2 entries (D7). */}
                          {!isDraft && (
                            <div
                              className="mt-0.5 flex items-center justify-end gap-1 font-mono"
                              data-testid={`library-trend-${it.id}`}
                              style={{ fontSize: 10 }}
                            >
                              {(!trend || trend.count < 2) ? (
                                <span className="text-ink-4 italic" style={{ fontSize: 10 }}>
                                  No history yet
                                </span>
                              ) : (
                                <span
                                  className={cls(
                                    "inline-flex items-center gap-0.5",
                                    trend.direction === "up" ? "text-note-red"
                                    : trend.direction === "down" ? "text-note-green"
                                    : "text-ink-4"
                                  )}
                                  title={`${gbp(trend.earliest)} → ${gbp(trend.latest)} over ${trend.count} change${trend.count === 1 ? "" : "s"}`}
                                >
                                  {trend.direction === "up" && <TrendingUp size={10} />}
                                  {trend.direction === "down" && <TrendingDown size={10} />}
                                  {trend.direction === "flat" && <Minus size={10} />}
                                  {trend.deltaPct != null ? `${trend.deltaPct > 0 ? "+" : ""}${trend.deltaPct.toFixed(1)}%` : "—"}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        {status === "draft" ? (
                          <td className="px-4 py-3 text-right">
                            <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>
                              {it.source_frequency || 0}
                            </span>
                          </td>
                        ) : (
                          <td className="px-4 py-3">
                            <span className="font-mono tnum text-ink-3" style={{ fontSize: 12 }}>
                              {ukDate(it.updated_at)}
                            </span>
                          </td>
                        )}
                        <td className="pr-4 py-3 text-right whitespace-nowrap">
                          {isDraft ? (
                            <div className="inline-flex items-center gap-1">
                              <button
                                data-row-action
                                onClick={(e) => { e.stopPropagation(); setFlatRateDialog(it); }}
                                data-testid={`library-promote-flat-${it.id}`}
                                className="inline-flex items-center gap-1 h-6 px-2 rounded border border-line hover:border-accent hover:text-accent-deep bg-surface transition-colors"
                                style={{ fontSize: 11 }}
                              >
                                <ArrowUpFromLine size={10} /> Flat rate
                              </button>
                              <button
                                data-row-action
                                onClick={(e) => { e.stopPropagation(); setDraftDeleteDialog(it); }}
                                data-testid={`library-delete-draft-${it.id}`}
                                className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center h-6 w-6 rounded border border-line hover:border-red-500 hover:text-red-600 bg-surface"
                                style={{ fontSize: 11 }}
                                title="Delete this draft (hard delete)"
                                aria-label="Delete draft"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          ) : (
                            // Live rows get an inline demote action (available on ALL live items)
                            <button
                              data-row-action
                              onClick={(e) => { e.stopPropagation(); setDemoteDialog(it); }}
                              data-testid={`library-demote-${it.id}`}
                              className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 h-6 px-2 rounded border border-line hover:border-warning hover:text-warning bg-surface"
                              style={{ fontSize: 11 }}
                              title="Demote back to Draft"
                            >
                              <ArrowDownToLine size={10} /> Demote
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Import CSV modal */}
      <LibraryImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onCommitted={onImportCommitted}
      />

      {/* Promote-as-flat-rate dialog */}
      <PromoteFlatDialog
        open={!!flatRateDialog}
        item={flatRateDialog}
        onClose={() => setFlatRateDialog(null)}
        onConfirm={performPromoteFlat}
      />

      {/* Demote dialog */}
      <DemoteDialog
        open={!!demoteDialog}
        item={demoteDialog}
        onClose={() => setDemoteDialog(null)}
        onConfirm={performDemote}
      />

      {/* Single-draft hard-delete dialog */}
      <DraftDeleteDialog
        open={!!draftDeleteDialog}
        item={draftDeleteDialog}
        onClose={() => setDraftDeleteDialog(null)}
        onConfirm={performDraftDelete}
      />

      {/* Bulk phase tag dialog */}
      <BulkTagDialog
        open={bulkTagDialog}
        count={selectedIds.size}
        phases={masterPhases}
        onClose={() => setBulkTagDialog(false)}
        onConfirm={performBulkTag}
      />

      {/* Bulk delete confirmation */}
      <BulkDeleteDialog
        open={bulkDeleteDialog}
        count={selectedIds.size}
        onClose={() => setBulkDeleteDialog(false)}
        onConfirm={performBulkDelete}
      />

      {/* LT-28 — Stale review modal */}
      <StaleReviewModal
        open={staleReviewOpen}
        onClose={() => setStaleReviewOpen(false)}
        onCommitted={async () => {
          // Re-fetch everything the modal might have changed: items, counts,
          // ledger cache (the trend column reads that).
          await Promise.all([load(status), loadCounts(), loadLedgerCache()]);
        }}
      />
    </div>
  );
}

/* ---------------- dialogs ---------------- */

function PromoteFlatDialog({ open, item, onClose, onConfirm }) {
  const [rate, setRate] = useState("");
  useEffect(() => {
    if (open && item) {
      // Pre-fill with the imported reference rate as a convenience.
      setRate(item.ref_rate_total != null ? String(item.ref_rate_total) : "");
    }
  }, [open, item]);
  const canSubmit = Number(rate) > 0;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Promote as flat rate"
      testid="library-promote-flat-modal"
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="promote-flat-cancel">Cancel</QuietButton>
          <PrimaryButton
            onClick={() => onConfirm(item, rate)}
            disabled={!canSubmit}
            icon={ArrowUpFromLine}
            data-testid="promote-flat-confirm"
          >
            Promote to Live
          </PrimaryButton>
        </>
      }
    >
      <p className="text-ink-3 mb-4" style={{ fontSize: 12.5 }}>
        Promote <strong className="text-ink">{item?.description}</strong> to Live with the flat
        rate below. This item will be usable in tenders immediately. It will remain marked
        as <strong>Not built up</strong> until a recipe is added.
      </p>
      <div className="mb-4">
        <label className="block col-head text-ink-3 mb-1.5" style={{ fontSize: 10.5 }}>
          Flat rate (£ per {item?.unit || "unit"})
        </label>
        <Input
          value={rate}
          onChange={setRate}
          type="text"
          mono
          data-testid="promote-flat-rate-input"
          placeholder="0.00"
        />
        {item?.ref_rate_total != null && (
          <p className="text-ink-4 mt-1.5" style={{ fontSize: 11 }}>
            Imported reference: {gbp(item.ref_rate_total)} (editable — confirm the actual rate before promoting).
          </p>
        )}
      </div>
    </Modal>
  );
}

function DemoteDialog({ open, item, onClose, onConfirm }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Demote to Draft"
      testid="library-demote-modal"
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="demote-cancel">Cancel</QuietButton>
          <PrimaryButton
            onClick={() => onConfirm(item)}
            icon={ArrowDownToLine}
            data-testid="demote-confirm"
          >
            Demote
          </PrimaryButton>
        </>
      }
    >
      <p className="text-ink-3" style={{ fontSize: 12.5 }}>
        Return <strong className="text-ink">{item?.description}</strong> to Draft. It will be
        hidden from all tender library pickers until promoted back to Live. The recipe and
        reference data are preserved.
      </p>
    </Modal>
  );
}

function DraftDeleteDialog({ open, item, onClose, onConfirm }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete draft item?"
      testid="library-draft-delete-modal"
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="draft-delete-cancel">Cancel</QuietButton>
          <PrimaryButton
            onClick={() => onConfirm(item)}
            icon={Trash2}
            data-testid="draft-delete-confirm"
            className="!bg-red-700 hover:!bg-red-800"
          >
            Delete permanently
          </PrimaryButton>
        </>
      }
    >
      <p className="text-ink-3" style={{ fontSize: 12.5 }}>
        This is a <strong>hard delete</strong> — draft items are candidates with no history
        worth preserving. If you meant to keep the item but hide it from tenders, use
        <strong> Promote as flat rate</strong> then <strong>Demote</strong> instead. Live
        items cannot be hard-deleted (they must be archived).
      </p>
      {item && (
        <div className="mt-4 px-3 py-2.5 rounded-md border border-line bg-surface-2/40">
          <div className="col-head text-ink-3" style={{ fontSize: 10 }}>Deleting</div>
          <div className="text-ink mt-1" style={{ fontSize: 13, fontWeight: 500 }}>
            {item.description}
          </div>
          {item.sku && (
            <div className="text-ink-4 font-mono tnum mt-0.5" style={{ fontSize: 11 }}>
              {item.sku}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function BulkTagDialog({ open, count, phases, onClose, onConfirm }) {
  const [phase, setPhase] = useState("");
  useEffect(() => { if (open) setPhase(""); }, [open]);
  const options = useMemo(
    () => phases.map((p) => ({ value: p.name, label: p.name })),
    [phases],
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Assign phase tag to ${count} item${count === 1 ? "" : "s"}`}
      testid="library-bulk-tag-modal"
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="bulk-tag-cancel">Cancel</QuietButton>
          <PrimaryButton
            onClick={() => onConfirm(phase)}
            disabled={!phase}
            icon={Tags}
            data-testid="bulk-tag-confirm"
          >
            Apply
          </PrimaryButton>
        </>
      }
    >
      <p className="text-ink-3 mb-4" style={{ fontSize: 12.5 }}>
        Overwrites existing phase tags on the selected draft items with the single tag chosen below.
      </p>
      <label className="block col-head text-ink-3 mb-1.5" style={{ fontSize: 10.5 }}>Phase tag</label>
      <Select
        value={phase}
        onChange={setPhase}
        options={options}
        placeholder="Select a phase…"
        data-testid="bulk-tag-select"
      />
    </Modal>
  );
}

function BulkDeleteDialog({ open, count, onClose, onConfirm }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Delete ${count} draft item${count === 1 ? "" : "s"}?`}
      testid="library-bulk-delete-modal"
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="bulk-delete-cancel">Cancel</QuietButton>
          <PrimaryButton
            onClick={onConfirm}
            icon={Trash2}
            data-testid="bulk-delete-confirm"
            className="!bg-red-700 hover:!bg-red-800"
          >
            Delete permanently
          </PrimaryButton>
        </>
      }
    >
      <p className="text-ink-3" style={{ fontSize: 12.5 }}>
        This is a <strong>hard delete</strong> — draft items are candidates with no history worth
        preserving. Live items cannot be bulk-deleted (they must be archived individually).
      </p>
    </Modal>
  );
}
